import * as React from 'react';
import { useDispatch } from 'react-redux';

import { fetchPreviewOffer } from '../../../../../common/actions/fetchPreviewOffer/fetchPreviewOffer';
import { IDailyRentOfferForMobileSite } from '../../../../../common/repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { TThunkDispatch } from '../../../../../common/types/redux';
import { EFavoriteAction, useFavoritesSubscriber } from '../../../../../common/utils/favorites';

interface IUseLocalPreviewOfferParams {
  realtyId: number | null | undefined;
  isListingOffersLoading: boolean;
  listingOffer: IDailyRentOfferForMobileSite | undefined;
}

interface IUseLocalPreviewOfferResult {
  isPreviewOfferLoading: boolean;
  offer: IDailyRentOfferForMobileSite | undefined;
  retryPreviewOffer(): void;
}

function getPreviewOffer(offer: IDailyRentOfferForMobileSite | undefined, realtyId: number | null | undefined) {
  if (!offer || !realtyId) {
    return undefined;
  }

  return offer.id === realtyId ? offer : undefined;
}

// Нужно при работе с закрытым листингом, в таком случае оффера нет в листинге и его надо загрузить специально для превью
export function useLocalPreviewOffer({
  realtyId,
  isListingOffersLoading,
  listingOffer,
}: IUseLocalPreviewOfferParams): IUseLocalPreviewOfferResult {
  const reduxDispatch = useDispatch<TThunkDispatch>();
  const requestedPreviewOfferIdRef = React.useRef<number | undefined>(undefined);
  const cachedListingOfferIdRef = React.useRef<number | null | undefined>(undefined);
  const [localPreviewOffer, setLocalPreviewOffer] = React.useState<IDailyRentOfferForMobileSite | undefined>();
  const [isLocalPreviewOfferLoading, setIsLocalPreviewOfferLoading] = React.useState(false);
  const lastFavoritesAction = useFavoritesSubscriber();

  // Listing имеет приоритет: локальный offer нужен только как fallback для preview.
  const localPreviewOfferForCurrentPin = getPreviewOffer(localPreviewOffer, realtyId);
  const offer = listingOffer || localPreviewOfferForCurrentPin;

  // Кэшируем listingOffer один раз на пин: при закрытии списка он пропадёт из listingOffer, и без
  // кэша превью моргнёт (offer → undefined → loading → догрузка). Пишем однократно (guard по realtyId) —
  // пока список открыт, показывается сам listingOffer (приоритетнее кэша), поэтому держать кэш свежим
  // не нужно; избранное для закрытого списка досинхронизирует favorites-эффект ниже.
  React.useEffect(() => {
    if (listingOffer && cachedListingOfferIdRef.current !== realtyId) {
      cachedListingOfferIdRef.current = realtyId;
      setLocalPreviewOffer(listingOffer);
    }
  }, [realtyId, listingOffer]);

  const shouldFetchLocalPreviewOffer = Boolean(
    realtyId && requestedPreviewOfferIdRef.current !== realtyId && !offer && !isListingOffersLoading,
  );
  const isPreviewOfferLoading = isListingOffersLoading || shouldFetchLocalPreviewOffer || isLocalPreviewOfferLoading;

  const loadPreviewOffer = React.useCallback(
    (offerId: number) => {
      requestedPreviewOfferIdRef.current = offerId;
      setIsLocalPreviewOfferLoading(true);

      // При быстром листании устаревший ответ не должен затирать оффер и loading нового запроса.
      const isLastRequested = () => requestedPreviewOfferIdRef.current === offerId;

      Promise.resolve(reduxDispatch(fetchPreviewOffer(offerId)))
        .then(previewOffer => {
          if (isLastRequested()) {
            setLocalPreviewOffer(previewOffer);
          }
        })
        .catch(() => {
          if (isLastRequested()) {
            setLocalPreviewOffer(undefined);
          }
        })
        .finally(() => {
          if (isLastRequested()) {
            setIsLocalPreviewOfferLoading(false);
          }
        });
    },
    [reduxDispatch],
  );

  React.useEffect(() => {
    if (!shouldFetchLocalPreviewOffer || !realtyId) {
      return;
    }

    loadPreviewOffer(realtyId);
  }, [realtyId, loadPreviewOffer, shouldFetchLocalPreviewOffer]);

  // Локальный preview-оффер не в redux, поэтому правки избранного к нему применяем через подписку на favorites-экшены
  React.useEffect(() => {
    if (!lastFavoritesAction) {
      return;
    }

    setLocalPreviewOffer(prev =>
      prev && prev.id === lastFavoritesAction.offerId
        ? { ...prev, isFavorite: lastFavoritesAction.action === EFavoriteAction.Added }
        : prev,
    );
  }, [lastFavoritesAction]);

  const retryPreviewOffer = React.useCallback(() => {
    if (!realtyId) {
      return;
    }

    loadPreviewOffer(realtyId);
  }, [realtyId, loadPreviewOffer]);

  return {
    isPreviewOfferLoading,
    offer,
    retryPreviewOffer,
  };
}
